/**
 * Sender Keys — group E2EE without N pairwise encryptions per message.
 *
 * In Signal's group protocol, each sender holds a per-group SenderKey:
 * a 32-byte chain key plus an Ed25519 signing keypair. The chain key
 * ratchets forward HKDF-style on every send, producing a fresh AES-GCM
 * message key for each ciphertext. Receivers store the latest chain
 * state per (groupId, senderId, deviceId) and ratchet it forward
 * locally to derive the same message keys.
 *
 * Properties this primitive must hold:
 *   - One-way ratchet: deriving messageKey N must not let an attacker
 *     work backwards to chainKey N-1. Achieved via HKDF — message-key
 *     and next-chain-key are independent outputs from the current
 *     chain key under different `info` strings.
 *   - One key per message: AES-GCM nonce can be zeros because each
 *     message key is fresh; reuse is not a concern.
 *   - Bounded lookahead: receivers may need to advance the chain past
 *     missed messages, but never more than `maxLookahead` steps so a
 *     hostile message claiming chainIndex = 2^31 cannot DoS us.
 *
 * ## iOS parity
 * Mirrors `kaether_for_ios/Stoat/E2EE/SenderKey.swift` exactly.
 * HKDF info strings are byte-for-byte identical:
 *   - Message key: `kaether:sk:msgkey:v1:<idx>`
 *   - Next chain key: `kaether:sk:next:v1`
 *
 * Pure logic + SubtleCrypto only. No SolidJS imports.
 */

// ─── types ────────────────────────────────────────────────────────────────────

export interface SenderKeyId {
  readonly groupId: string;
  readonly senderId: string;
  readonly deviceId: number;
}

/**
 * Per-(group, sender, device) ratchet state.
 *
 * `signingPrivateKeyBytes` is non-null only for the local owner of the
 * key — receivers store public-key-only state and cannot forge messages
 * under our identity.
 */
export interface SenderKeyState {
  /** Raw 32-byte chain key. */
  readonly chainKeyBytes: Uint8Array;
  readonly chainIndex: number;
  /** Raw 32-byte Ed25519 public key. */
  readonly signingPublicKeyBytes: Uint8Array;
  /** Raw 32-byte Ed25519 private key seed. Null on receiver state. */
  readonly signingPrivateKeyBytes: Uint8Array | null;
}

// ─── errors ───────────────────────────────────────────────────────────────────

export class SenderKeyError extends Error {
  constructor(
    public readonly kind:
      | "invalidChainKeyLength"
      | "signatureVerificationFailed"
      | "lookaheadLimitExceeded"
      | "decryptionFailed"
      | "malformedMessage"
      | "missingState"
      | "unsupportedSchema",
    detail: string,
  ) {
    super(`[SenderKey:${kind}] ${detail}`);
    this.name = "SenderKeyError";
  }
}

// ─── chain constants ──────────────────────────────────────────────────────────

export const CHAIN_KEY_LENGTH = 32;
export const MAX_LOOKAHEAD = 1000;

const enc = new TextEncoder();

// ─── HKDF helpers ─────────────────────────────────────────────────────────────

/**
 * Import a raw 32-byte chain key as an HKDF key material.
 * We use `deriveBits` rather than `deriveKey` so we can control the output
 * length and get raw bytes back.
 */
async function hkdfSha256(
  inputKeyBytes: Uint8Array,
  infoStr: string,
  outputBytes: number,
): Promise<Uint8Array> {
  const baseKey = await crypto.subtle.importKey(
    "raw",
    inputKeyBytes,
    { name: "HKDF" },
    false,
    ["deriveBits"],
  );
  const salt = new Uint8Array(32); // all-zeros, 32 bytes — mirrors iOS
  const info = enc.encode(infoStr);
  const bits = await crypto.subtle.deriveBits(
    { name: "HKDF", hash: "SHA-256", salt, info },
    baseKey,
    outputBytes * 8,
  );
  return new Uint8Array(bits);
}

// ─── SenderKeyChain ───────────────────────────────────────────────────────────

/**
 * HKDF-SHA256 input chainKey, info `kaether:sk:msgkey:v1:<index>`, 32 bytes.
 * Independent from `advanceChainKey` so an attacker who learns the message
 * key cannot recover the chain key.
 *
 * Info string is byte-for-byte identical to iOS.
 */
export async function deriveMessageKey(
  chainKeyBytes: Uint8Array,
  index: number,
): Promise<Uint8Array> {
  return hkdfSha256(chainKeyBytes, `kaether:sk:msgkey:v1:${index}`, 32);
}

/**
 * HKDF-SHA256 input chainKey, info `kaether:sk:next:v1`, 32 bytes.
 * Replaces the chain key after every send / open. One-way: knowing the
 * new key reveals nothing about the previous one.
 *
 * Info string is byte-for-byte identical to iOS.
 */
export async function advanceChainKey(
  chainKeyBytes: Uint8Array,
): Promise<Uint8Array> {
  return hkdfSha256(chainKeyBytes, "kaether:sk:next:v1", 32);
}

/**
 * Validates a chain key against CHAIN_KEY_LENGTH. Throws so callers
 * surface a malformed-state error instead of silently producing wrong-shaped
 * HKDF outputs downstream.
 */
export function validateChainKeyLength(chainKeyBytes: Uint8Array): void {
  if (chainKeyBytes.byteLength !== CHAIN_KEY_LENGTH) {
    throw new SenderKeyError(
      "invalidChainKeyLength",
      `expected ${CHAIN_KEY_LENGTH} bytes, got ${chainKeyBytes.byteLength}`,
    );
  }
}

/**
 * Generates a fresh SenderKey: random 32-byte chain key + Ed25519
 * signing keypair, chainIndex = 0. Only the local owner ever
 * receives the private signing key — receivers strip it before
 * persisting.
 *
 * Chrome refuses `exportKey("raw", privateKey)` for Ed25519; the private key
 * must be exported as PKCS#8 and the 32-byte seed extracted from the tail of
 * the blob (the last 32 bytes of the constant-prefix structure).
 */
export async function generateSenderKeyState(): Promise<SenderKeyState> {
  const chainKeyBytes = crypto.getRandomValues(new Uint8Array(CHAIN_KEY_LENGTH));

  const kp = await crypto.subtle.generateKey(
    { name: "Ed25519" },
    true,
    ["sign", "verify"],
  );
  const pubBuf = await crypto.subtle.exportKey("raw", kp.publicKey);
  // PKCS#8 for Ed25519: 16-byte header + 32-byte seed.
  const privPkcs8 = new Uint8Array(
    await crypto.subtle.exportKey("pkcs8", kp.privateKey),
  );
  const privSeed = privPkcs8.slice(privPkcs8.length - 32);

  return {
    chainKeyBytes: chainKeyBytes.slice(),
    chainIndex: 0,
    signingPublicKeyBytes: new Uint8Array(pubBuf),
    signingPrivateKeyBytes: privSeed,
  };
}

/**
 * PKCS#8 prefix wrapping a 32-byte Ed25519 seed. Chrome's WebCrypto refuses
 * `importKey("raw", ..., ["sign"])` for Ed25519 — private keys must arrive as
 * PKCS#8. Total wrapped length = 48 bytes.
 */
const ED25519_PKCS8_PREFIX = new Uint8Array([
  0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06,
  0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20,
]);

/** Wrap a raw 32-byte Ed25519 seed in PKCS#8 DER. Shared with SKDM / SKMessage. */
export function wrapEd25519SeedAsPkcs8(seed: Uint8Array): Uint8Array {
  const out = new Uint8Array(ED25519_PKCS8_PREFIX.length + seed.length);
  out.set(ED25519_PKCS8_PREFIX, 0);
  out.set(seed, ED25519_PKCS8_PREFIX.length);
  return out;
}

/**
 * Return a receiver-side view of a state: same chain, same public key,
 * but `signingPrivateKeyBytes` stripped to null. Receivers can verify
 * but cannot forge messages under the sender's identity.
 */
export function receiverView(state: SenderKeyState): SenderKeyState {
  return {
    chainKeyBytes: state.chainKeyBytes,
    chainIndex: state.chainIndex,
    signingPublicKeyBytes: state.signingPublicKeyBytes,
    signingPrivateKeyBytes: null,
  };
}
